export const commodities = {
    id: 'commodities',
    title: 'Commodities',
    summary: 'What tends to move gold, oil, silver and other metals, and natural gas, and how commodity trading hours and weekend gaps affect your trades.',
    level: 'intermediate',
    art: 'gold',
    image: { src: '/academy/commodities.webp', alt: 'Gold bars beside an oil barrel and a silver ingot on a dark floor' },
    screen: { to: '/markets', label: 'Markets' },
    lessons: [
        {
            id: 'gold-safe-haven',
            title: 'Gold: safe haven, the dollar and real yields',
            minutes: 5,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'Gold has been a store of value for thousands of years. Investors often buy it when they are worried about markets, wars or the value of money, which is why it is called a **safe haven**. Central banks also hold gold in their reserves and buy or sell it.' },
                { type: 'p', text: 'Gold is priced in US dollars. When the dollar strengthens, gold costs more for buyers using other currencies, which has often weighed on its price. A weaker dollar has often helped it.' },
                { type: 'p', text: 'Gold pays no interest. The **real yield** is the interest rate on a safe bond minus inflation. When real yields rise, holding gold instead of bonds costs more in lost interest, and gold has often struggled. When real yields fall, that cost shrinks.' },
                { type: 'example', title: 'A real yield', text: 'A government bond pays 4.5% a year and inflation is 2.5%. The real yield is 4.5% − 2.5% = **2.0%**. If inflation rose to 3.5% with the same bond rate, the real yield would fall to 1.0%.' },
                { type: 'example', title: 'A gold trade', text: 'You buy 5 oz of GOLD/USDT at $2,650, a $13,250 trade. Gold rises 1.5% to $2,689.75. The profit is 5 × $39.75 = **$198.75**. A 1.5% fall would have cost the same.' },
                { type: 'warn', text: 'These are tendencies, not rules. Gold can fall in a panic when investors sell everything to raise cash, and it can rise with a strong dollar.' },
            ],
            quiz: [
                {
                    q: 'Bond rates are 3% and inflation is 4%. What is the real yield?',
                    options: ['7%', '1%', '−1%'],
                    answer: 2,
                    why: 'Real yield = interest rate − inflation = 3% − 4% = −1%.',
                },
                {
                    q: 'Why do rising real yields tend to weigh on gold?',
                    options: ['Gold pays no interest, so holding it costs more in lost interest', 'Gold mines close', 'Gold is priced in euros'],
                    answer: 0,
                    why: 'When safe bonds pay more after inflation, the interest you give up by holding gold grows.',
                },
            ],
            tryIt: { to: '/trade/GOLD', label: 'Open GOLD/USDT' },
            terms: ['safe-haven', 'real-yield'],
        },
        {
            id: 'oil-opec-inventories',
            title: 'Oil: OPEC+, inventories and geopolitics',
            minutes: 5,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'You can trade two benchmark oils: **OIL/USDT** (WTI crude, the main US price) and **BRENT/USDT** (Brent crude, the main international price). They usually move together, and their prices are usually close.' },
                {
                    type: 'list',
                    items: [
                        '**OPEC+**: the oil-producing countries of OPEC and allies such as Russia agree how much oil to pump. Changes to their output targets can move the price sharply.',
                        '**Inventories**: oil held in storage. US stock figures are published every week; a bigger-than-expected rise in stocks suggests weak demand, a bigger-than-expected fall suggests strong demand.',
                        '**Geopolitics**: conflict near producing regions or shipping routes can threaten supply and lift prices quickly.',
                        '**The economy**: factories, travel and transport all use oil, so signs of slower growth tend to weigh on it.',
                    ],
                },
                { type: 'example', title: 'An inventory surprise', text: 'You buy 200 barrels of OIL/USDT at $72.00. The weekly figures show a much larger rise in US stocks than expected, and oil falls to $70.50. The loss is 200 × $1.50 = **$300**.' },
                { type: 'p', text: 'Oil can move several percent in a day on a headline. Check the size of a barrel move in dollars before you trade: the order panel shows the **Value of $1 move** for the size you enter.' },
                { type: 'warn', text: 'Headlines about supply can arrive at any hour, including while the market is closed. The price can then open far from where it closed.' },
            ],
            quiz: [
                {
                    q: 'US oil stocks rise much more than expected. What does that usually suggest?',
                    options: ['Demand is weaker than expected', 'OPEC+ has cut output', 'The market will close early'],
                    answer: 0,
                    why: 'Oil piling up in storage suggests less is being used than expected.',
                },
                {
                    q: 'You hold 100 barrels and oil rises $2. What is the profit?',
                    options: ['$2', '$100', '$200'],
                    answer: 2,
                    why: '100 barrels × $2 = $200.',
                },
            ],
            tryIt: { to: '/markets', label: 'Find oil in Commodities' },
            terms: ['opec', 'inventories'],
        },
        {
            id: 'silver-industrial-metals',
            title: 'Silver and industrial metals',
            minutes: 4,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: '**Silver** is both a precious metal and an industrial one. It often follows gold, but much of it is used in electronics and solar panels, so it also reacts to the economy. It usually moves more than gold in percentage terms.' },
                { type: 'p', text: 'The **gold/silver ratio** is the gold price divided by the silver price: how many ounces of silver buy one ounce of gold. Traders watch it to compare the two metals.' },
                { type: 'example', title: 'The ratio in numbers', text: 'Gold is $2,650 and silver $31. The ratio is 2,650 ÷ 31 = **85.5**. If silver rose to $33 and gold stayed put, the ratio would fall to 80.3: silver did better than gold.' },
                {
                    type: 'list',
                    items: [
                        '**Copper** is used in building, power grids and electrical goods. Its price is often read as a gauge of factory activity, and China is the largest buyer.',
                        '**Platinum** and **palladium** are used mainly in car exhaust systems, so car production matters to both.',
                    ],
                },
                { type: 'tip', text: 'Metals have very different contract sizes. Check the contract size and the notional in the order panel before you trade one you have not traded before.' },
            ],
            quiz: [
                {
                    q: 'Gold is $2,400 and silver $30. What is the gold/silver ratio?',
                    options: ['30', '80', '2,430'],
                    answer: 1,
                    why: '$2,400 ÷ $30 = 80 ounces of silver for one ounce of gold.',
                },
                {
                    q: 'Why does silver react to the economy more than gold does?',
                    options: ['A large share of silver is used by industry', 'Silver is priced in yen', 'Silver only trades on weekdays'],
                    answer: 0,
                    why: 'Industrial use, in electronics and solar panels, ties silver demand to economic activity.',
                },
            ],
            tryIt: { to: '/markets', label: 'Compare the metals' },
            terms: ['industrial-metal', 'gold-silver-ratio', 'lot-size'],
        },
        {
            id: 'natural-gas-seasons',
            title: 'Natural gas and the seasons',
            minutes: 4,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: '**Natural gas** (NATGAS/USDT) is used to heat homes and to generate electricity. Demand rises in cold winters for heating and in hot summers for air conditioning, so the weather moves the price more than for most markets.' },
                { type: 'p', text: 'In the US, gas is put into storage in the warmer months and drawn out over winter. A weekly US storage report compares the change with what was expected, and a surprise can move the price within minutes.' },
                { type: 'example', title: 'A cold forecast', text: 'You buy 1,000 MMBtu of natural gas at $3.40, a $3,400 trade. Forecasts turn colder and the price rises 8% to $3.672. The profit is 1,000 × $0.272 = **$272**. A warmer forecast could have cost as much.' },
                { type: 'p', text: 'Seasonal patterns are widely known, so they are usually already in the price. What moves gas is weather that turns out different from the forecast, and storage that differs from expectations.' },
                { type: 'warn', text: 'Natural gas is one of the most volatile markets you can trade. Moves of 5% or more in a day are common. Use a smaller size and a stop loss.' },
            ],
            quiz: [
                {
                    q: 'Why does winter matter for natural gas?',
                    options: ['Heating demand rises in the cold', 'The market is closed in winter', 'Swap is not charged in winter'],
                    answer: 0,
                    why: 'Gas heats homes, so cold weather raises demand.',
                },
                {
                    q: 'A seasonal pattern is known to everyone. What usually moves the price?',
                    options: ['The calendar date alone', 'Weather and storage that differ from what was expected', 'Nothing: gas never moves'],
                    answer: 1,
                    why: 'Known patterns are priced in. Surprises in weather and storage are what move it.',
                },
            ],
            tryIt: { to: '/markets', label: 'Find natural gas' },
            terms: ['seasonality'],
        },
        {
            id: 'commodity-hours-gaps',
            title: 'Commodity hours and weekend gaps',
            minutes: 4,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'Commodities here normally trade from **Sunday 18:00 to Friday 17:00 New York time**, with a short break every day from 17:00 to 18:00. They are closed at the weekend. The instrument screen shows whether a market is open.' },
                { type: 'p', text: 'While a market is closed, you cannot open or close trades in it. Your stop losses, take profits and pending orders wait for the first price after it reopens.' },
                { type: 'example', title: 'A weekend gap on oil', text: 'You hold 100 barrels of OIL/USDT bought at $72.00, with a stop loss at $71.00, a planned loss of $100. Over the weekend there is news about supply, and oil opens on Sunday at $69.50. The stop closes at about $69.50: a loss of about **$250**.' },
                { type: 'p', text: 'Commodities are not charged swap on Saturday and Sunday. Instead, trades held past {swapHour} on {swapTripleDay} are charged three days of swap.' },
                { type: 'warn', text: 'A **gap** can jump straight past your stop. If you can’t afford the gap, reduce the size or close before the weekend.' },
                { type: 'tip', text: 'You can set a **Market opens** alert from the bell on any row of the Markets page, so you know as soon as trading starts again.' },
            ],
            quiz: [
                {
                    q: 'Your gold stop loss is at $2,600. Gold closes Friday at $2,620 and opens Sunday at $2,580. Where does the trade close?',
                    options: ['At $2,600', 'At about $2,580, the first price after the open', 'It stays open until you close it'],
                    answer: 1,
                    why: 'A stop fills at the next available price. After a gap, that can be worse than your level.',
                },
                {
                    q: 'When is a gold trade charged three days of swap?',
                    options: ['On Saturday', 'On {swapTripleDay}', 'Never'],
                    answer: 1,
                    why: 'Commodities skip the weekend and are charged ×3 on {swapTripleDay} instead.',
                },
            ],
            tryIt: { to: '/trade/GOLD', label: 'Check when gold is open' },
            terms: ['gap', 'trading-hours', 'triple-swap', 'stop-loss'],
        },
    ],
};
export const commoditiesTerms = [
    { id: 'safe-haven', term: 'Safe haven', text: 'An asset investors tend to buy when they are worried, such as gold. It is a tendency, not a guarantee: safe havens can fall too.', lesson: 'gold-safe-haven' },
    { id: 'real-yield', term: 'Real yield', text: 'The interest rate on a safe bond minus inflation. Rising real yields make holding gold, which pays no interest, more costly.', lesson: 'gold-safe-haven' },
    { id: 'opec', term: 'OPEC+', text: 'A group of oil-producing countries, OPEC and allies such as Russia, that agree output targets. Changes to them can move oil prices sharply.', lesson: 'oil-opec-inventories' },
    { id: 'inventories', term: 'Inventories (oil stocks)', text: 'Oil held in storage, reported weekly in the US. A bigger rise than expected suggests weak demand; a bigger fall suggests strong demand.', lesson: 'oil-opec-inventories' },
    { id: 'industrial-metal', term: 'Industrial metal', text: 'A metal used mainly by industry, such as copper. Its price tends to follow factory activity and building.', lesson: 'silver-industrial-metals' },
    { id: 'gold-silver-ratio', term: 'Gold/silver ratio', text: 'The gold price ÷ the silver price: how many ounces of silver buy one ounce of gold. Used to compare the two metals.', lesson: 'silver-industrial-metals' },
    { id: 'seasonality', term: 'Seasonality', text: 'A pattern that tends to repeat at the same time of year, such as more demand for natural gas in winter. Known patterns are usually already in the price.', lesson: 'natural-gas-seasons' },
];
//# sourceMappingURL=commodities.js.map